import { Injectable, NgZone, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { IEventsRepository } from '../../../domain/events/i-events-repository';
import { WhatsappEvent } from '../../../domain/events/types';
import {
  IHttpClient,
  UNAUTHORIZED_CODE as UNAUTHORIZED_STATUS,
} from '../../services/httpClient/interface';
import { withQuery } from '../util';
import { mapWhatsappEvent } from './event-mapper';

const RECONNECT_DELAY_MS = 5000;

@Injectable({
  providedIn: 'root',
})
export class RealEventsRepository implements IEventsRepository {
  private httpClient = inject(IHttpClient);
  private ngZone = inject(NgZone);

  public connect(companyPhoneNumber: string): Observable<WhatsappEvent> {
    return new Observable<WhatsappEvent>(subscriber => {
      const controller = new AbortController();
      let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
      let closed = false;

      const run = async (): Promise<void> => {
        try {
          const url = this.httpClient.buildUrl(
            withQuery('/whatsapp/events', { companyPhoneNumber })
          );

          const response = await fetch(url, {
            method: 'GET',
            signal: controller.signal,
            headers: {
              accept: 'text/event-stream',
              Authorization: `Bearer ${this.httpClient.getToken()}`,
            },
          });

          if (response.status === UNAUTHORIZED_STATUS) {
            subscriber.error(new Error('SSE não autorizado'));
            return;
          }

          if (!response.ok || !response.body) {
            throw new Error(`SSE respondeu ${response.status}`);
          }

          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';

          while (true) {
            const { value, done } = await reader.read();

            if (done) {
              break;
            }

            buffer += decoder.decode(value, { stream: true });

            let separator = buffer.match(/\r?\n\r?\n/);

            while (separator) {
              const rawEvent = buffer.slice(0, separator.index);
              buffer = buffer.slice((separator.index ?? 0) + separator[0].length);

              const parsed = this.parseEvent(rawEvent);

              if (parsed) {
                this.ngZone.run(() => subscriber.next(parsed));
              }

              separator = buffer.match(/\r?\n\r?\n/);
            }
          }
        } catch (error) {
          if (controller.signal.aborted) {
            return;
          }

          console.warn('SSE de mensagens caiu, reconectando', error);
        }

        if (!closed) {
          reconnectTimer = setTimeout(run, RECONNECT_DELAY_MS);
        }
      };

      run();

      return () => {
        closed = true;

        if (reconnectTimer) {
          clearTimeout(reconnectTimer);
        }

        controller.abort();
      };
    });
  }

  private parseEvent(rawEvent: string): WhatsappEvent | null {
    const dataLines = rawEvent
      .split('\n')
      .map(line => line.trimEnd())
      .filter(line => line.startsWith('data:'))
      .map(line => line.slice(5).trim());

    if (dataLines.length === 0) {
      return null;
    }

    try {
      return mapWhatsappEvent(JSON.parse(dataLines.join('\n')));
    } catch {
      console.warn('Evento SSE inválido descartado');
      return null;
    }
  }
}
